import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../utils/axios';
import { 
  FileText, 
  Download, 
  Globe, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Upload,
  Languages
} from 'lucide-react';
import { OPENAI_SUPPORTED_LANGUAGES } from '../../utils/languages';
import { toast } from 'react-hot-toast';

interface MenuPDF {
  id: string;
  menuId: number;
  fileName: string;
  originalFileName: string;
  fileUrl: string;
  language: string;
  template: string;
  menuTitle: string;
  restaurantName: string;
  description: string;
  isPublic: boolean;
  downloadCount: number;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

const PdfGenerator: React.FC = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [generatedPdfs, setGeneratedPdfs] = useState<MenuPDF[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('it');
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [generatingLanguages, setGeneratingLanguages] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (menuId) {
      loadMenuData();
    }
  }, [menuId]);

  const loadMenuData = async () => {
    try {
      setLoading(true);
      
      // Get all PDFs for this menu
      const response = await axios.get(`/pdf/menu/${menuId}/all`);
      if (response.data.success) {
        setGeneratedPdfs(response.data.data || []);
      }
      
      // Get available languages (based on existing PDFs)
      const languagesSet = new Set<string>();
      response.data.data?.forEach((pdf: MenuPDF) => {
        languagesSet.add(pdf.language);
      });
      setAvailableLanguages(Array.from(languagesSet));
    } catch (error) {
      console.error('Error loading menu data:', error);
      toast.error('Failed to load menu data');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePdf = async (languageCode: string) => {
    setGeneratingLanguages(prev => new Set(prev).add(languageCode));
    
    try {
      // First translate the menu to the selected language
      const translationResponse = await axios.post(`/menu/${menuId}/translate`, {
        language: languageCode
      });
      
      if (!translationResponse.data.success) {
        throw new Error('Failed to translate menu');
      }
      
      // Then generate PDF with the translated content
      const formData = new FormData();
      formData.append('language', languageCode);
      formData.append('template', selectedTemplate);
      formData.append('description', `PDF menu for ${languageCode} language`);
      
      const pdfResponse = await axios.post(
        `/menu/${menuId}/upload-pdf`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (pdfResponse.data.success) {
        toast.success(`PDF generated for ${languageCode}`);
        // Reload the data to show the new PDF
        loadMenuData();
      } else {
        throw new Error(pdfResponse.data.message || 'Failed to generate PDF');
      }
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      toast.error(`Failed to generate PDF for ${languageCode}: ${error.message}`);
    } finally {
      setGeneratingLanguages(prev => {
        const newSet = new Set(prev);
        newSet.delete(languageCode);
        return newSet;
      });
    }
  };

  const handleDownloadPdf = (pdf: MenuPDF) => {
    window.open(pdf.fileUrl, '_blank');
  };

  const handleBulkGenerate = async () => {
    setGenerating(true);
    try {
      // Generate PDFs for all 36 languages
      const allLanguages = OPENAI_SUPPORTED_LANGUAGES.map(lang => lang.code);
      
      for (const languageCode of allLanguages) {
        await handleGeneratePdf(languageCode);
        // Add a small delay to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      toast.success('All PDFs generated successfully!');
    } catch (error) {
      console.error('Error in bulk generation:', error);
      toast.error('Error in bulk PDF generation');
    } finally {
      setGenerating(false);
    }
  };

  const getLanguageName = (code: string) => {
    const lang = OPENAI_SUPPORTED_LANGUAGES.find(l => l.code === code);
    return lang ? `${lang.flag} ${lang.nativeName} (${lang.name})` : code;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading PDF generator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            PDF Menu Generator
          </h1>
          <p className="text-gray-600 mt-2">
            Generate menu PDFs in multiple languages for your customers
          </p>
        </div>
        
        <button
          onClick={handleBulkGenerate}
          disabled={generating}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Languages className="w-5 h-5" />
              Generate All 36 Languages
            </>
          )}
        </button>
      </div>

      {/* Language Selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Generate PDF in Specific Language
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Language
            </label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {OPENAI_SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.nativeName} ({lang.name})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="mixology">mixology</option>
              <option value="doppia">Doppia</option>
              <option value="elegant">Elegant</option>
              <option value="minimalist">minimalist</option>
               <option value="custom">custom</option>
             
            </select>
          </div>
        </div>
        
        <button
          onClick={() => handleGeneratePdf(selectedLanguage)}
          disabled={generatingLanguages.has(selectedLanguage)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
        >
          {generatingLanguages.has(selectedLanguage) ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Generate PDF
            </>
          )}
        </button>
      </div>

      {/* Generated PDFs List */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Download className="w-5 h-5" />
          Generated PDFs ({generatedPdfs.length})
        </h2>
        
        {generatedPdfs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-[#7BD5B5]/40 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No PDFs generated yet</h3>
            <p className="text-gray-600">
              Generate your first PDF using the form above
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {generatedPdfs.map((pdf) => (
              <div 
                key={pdf.id} 
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {getLanguageName(pdf.language)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {new Date(pdf.createdAt).toLocaleDateString()} • {pdf.template} template
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    {pdf.downloadCount} downloads
                  </span>
                  <button
                    onClick={() => handleDownloadPdf(pdf)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Language Availability */}
      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Available Languages
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {OPENAI_SUPPORTED_LANGUAGES.map((lang) => {
            const isGenerated = generatedPdfs.some(pdf => pdf.language === lang.code);
            return (
              <div 
                key={lang.code}
                className={`p-3 rounded-lg border text-center ${
                  isGenerated 
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
              >
                <div className="text-2xl mb-1">{lang.flag}</div>
                <div className="text-xs font-medium">{lang.nativeName}</div>
                <div className="text-xs">{lang.name}</div>
                {isGenerated && (
                  <div className="mt-1 text-green-600 text-xs flex items-center justify-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Generated
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PdfGenerator;